/* eslint-disable no-template-curly-in-string */
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Typography,
  Select,
  message,
  Switch,
} from "antd";
import { getUserById, editUsers } from "../../../api/UserTypeAPI";
import { UserStatusEnum } from "../../../types/enums";
import authorization from "../../../auth/authorization";

const { Option } = Select;
const Title = styled(Typography.Title)`
  && {
    font-size: 36px;
    font-weight: 700;
  }
`;

const StyledButton = styled(Button)`
  margin: 8px 6px;
  width: 96px;
`;

const ButtonContainer = styled(Col)`
  display: flex;
  justify-content: flex-end;
  @media (max-width: 400px) {
    flex-direction: column;
    align-items: center;
  }
  > .ant-form-item {
    margin-bottom: unset;
  }
`;

const EditUser = (props) => {
  const [form] = Form.useForm();
  const {
    userId,
    afterEdit,
    clients,
    userRoles,
    updateUser,
    formId = "edit-user-form",
    hideActions = false,
    onLoadingChange,
  } = props;
  const [loading, setLoading] = useState(false);

  const setSubmitLoading = (isLoading) => {
    setLoading(isLoading);
    onLoadingChange?.(isLoading);
  };

  useEffect(() => {
    getUserById(userId)
      .then((res) => {
        if (res.data.success) {
          const isActive = res.data.payload.active === true && res.data.payload.status === UserStatusEnum.ACTIVE;
          form.setFieldsValue({ ...res.data.payload, active: isActive });
          message.success("Successfully loaded  User details");
        }
      })
      .catch(() => {
        message.error("Unable to fetch User details for Editing");
      });
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const onFinish = (values) => {
    onUpdatinguserType(userId, values);
  };

  const onUpdatinguserType = (id, userType) => {
    setSubmitLoading(true);
    userType.status = userType.active ? UserStatusEnum.ACTIVE : UserStatusEnum.DEACTIVE;
    editUsers(id, userType)
      .then((res) => {
        if (res.data.success) {
          message.success("Successfully Updated User Details");
          afterEdit();
          form.resetFields();
          updateUser();
          setSubmitLoading(false);
        } else {
          message.error(res.data.message);
          setSubmitLoading(false);
        }
      })
      .catch((error) => {
        message.error(error.response.data.message);
        setSubmitLoading(false);
      });
  };

  const layout = {
    labelCol: {
      span: 12,
    },
    wrapperCol: {
      span: 24,
    },
  };

  const onCancelingEdit = () => {
    afterEdit();
  };

  return (
    <Form
      {...layout}
      id={formId}
      form={form}
      layout={"vertical"}
      name="nest-messages"
      initialValues={props.selectedUser}
      onFinish={onFinish}
    >
      <Title level={2}>Edit User</Title>
      <Row gutter={24}>
        <Col xs={24} sm={24} md={24} lg={24} xl={12}>
          <Form.Item
            name="firstName"
            label="First Name"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <Input id="edit-user-first-name-input" size={"large"} placeholder="Enter the First Name" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={24} lg={24} xl={12}>
          <Form.Item
            name="lastName"
            label="Last Name"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <Input id="edit-user-last-name-input" size={"large"} placeholder="Enter the Last Name" />
          </Form.Item>
        </Col>

        <Col xs={24} sm={24} md={24} lg={24} xl={12}>
          <Form.Item
            name="email"
            label="E-Mail"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <Input id="edit-user-email-input" size={"large"} placeholder="Enter the E-Mail" />
          </Form.Item>
        </Col>

        <Col xs={24} sm={24} md={24} lg={24} xl={12}>
          <Form.Item
            name="phone"
            label="Phone No"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <Input id="edit-user-phone-input" size={"large"} placeholder="Enter the Phone No" />
          </Form.Item>
        </Col>

        <Col xs={24} sm={24} md={24} lg={24} xl={12}>
          <Form.Item
            name="userRoleId"
            label="User Role"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <Select
              id="edit-user-role-select"
              size={"large"}
              placeholder="Select User Role"
              getPopupContainer={(trigger) => trigger.parentElement}
              disabled={!authorization.isAllowed(props.userRoleType, "updateUserRoleDetails")}
            >
              {userRoles
                .filter((item) => ["ops admin", "it system admin", "developer"]
                .includes(item.name))
                .map((item) => (
                <Option
                  key={item._id}
                  value={item._id}
                >
                  {item.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        <Col xs={24} sm={24} md={24} lg={24} xl={12}>
          <Form.Item
            name="clients"
            label="Client"
            rules={[
              {
                required: true,
              },
            ]}
          >
            <Select
              id="edit-user-clients-select"
              mode="multiple"
              size={"large"}
              placeholder="Select Client"
              getPopupContainer={(trigger) => trigger.parentElement}
            >
              {clients.map((clientItem) => (
                <Option key={clientItem._id} value={clientItem._id}>
                  {clientItem.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item 
            style={{ width: "100%" }} 
            label="Active"
            name="active"
            valuePropName="checked"
          >
            <Switch 
              id="edit-user-active-switch"
              disabled={!props.canDeactivateUser} 
            />
          </Form.Item>
        </Col>

        {!hideActions ? (
          <ButtonContainer span={24}>
            <Form.Item wrapperCol={{ ...layout.wrapperCol }}>
              <StyledButton
                id="edit-user-cancel-btn"
                type="secondary"
                size={"large"}
                onClick={onCancelingEdit}
              >
                Cancel
              </StyledButton>
              <StyledButton
                id="edit-user-save-btn"
                type="primary"
                size={"large"}
                htmlType="submit"
                loading={loading}
              >
                Save
              </StyledButton>
            </Form.Item>
          </ButtonContainer>
        ) : null}
      </Row>
    </Form>
  );
};

export default EditUser;
